"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ExploreFavoriteDestinations() {
  const {
    ref: headerRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const highlights = [{
    date: "\xA0VALID from 01/07/2026 - 23/12/2026",
    title: "MAURITIUS ESCAPE",
    categorySlug: "mauritius-holidays",
    description: "Crystal clear lagoons and white sandy beaches await your arrival in paradise.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/images-4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/images-4.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/images-4.png",
    alt: "Tropical beach in Mauritius"
  }, {
    title: "MALDIVES LUXURY",
    categorySlug: "maldives-holidays",
    date: "VALID FROM 01/06/2026 - 01/03/2027",
    description: "Experience overwater villas and unparalleled privacy in the heart of the Indian Ocean with our Ayada Holiday Deal.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/Ayada-Maldives-villas-SUNSET-OCEAN-FAMILY-SUITE-5-scaled-1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/Ayada-Maldives-villas-SUNSET-OCEAN-FAMILY-SUITE-5-scaled-1.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/Ayada-Maldives-villas-SUNSET-OCEAN-FAMILY-SUITE-5-scaled-1.png",
    alt: "Overwater villa in the Maldives"
  }, {
    date: "VALID from 01/03/2027 - 01/08/2027",
    title: "EUROPEAN SUMMER",
    categorySlug: "europe-travel",
    description: "Wander through historic cobblestone streets and enjoy the Mediterranean sun with our 3 Top Cities in Europe package to visit LONDON + PARIS + ROME with tope excursions & 4 star accommodation plus flights. ",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/af5ce0_c7d79c96da6e42c297ff305c369ae495mv2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/af5ce0_c7d79c96da6e42c297ff305c369ae495mv2.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/af5ce0_c7d79c96da6e42c297ff305c369ae495mv2.png",
    alt: "Historic European city street"
  }, {
    date: "VALID FROM 01/07/2026 - 30-11/2026",
    title: "ZANZIBAR RETREAT",
    categorySlug: "zanzibar-holidays",
    description: "Discover the spice island's rich culture and stunning turquoise coastlines.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/zanzibar_coast.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/zanzibar_coast.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/zanzibar_coast.png-wvc-sizes",
    alt: "Zanzibar coastline view"
  }, {
    date: "VALID FROM 01/03/2026 - 30/11/2026",
    title: "THAILAND ADVENTURE",
    categorySlug: "thailand-holidays",
    description: "From bustling night markets to serene temples, explore the beauty of Thailand.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/thailand_temple.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/thailand_temple.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/thailand_temple.png-wvc-sizes",
    alt: "Traditional Thai temple"
  }, {
    date: "VALID FROM 01/05/2026 - 01/10/2026",
    title: "BALI ESCAPADE",
    categorySlug: "bali-holidays",
    description: "Find your zen in lush rice terraces and vibrant cultural landscapes.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/bali_terraces.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/bali_terraces.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/bali_terraces.png-wvc-sizes",
    alt: "Bali rice terraces"
  }];
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const handleScroll = () => {
      const cards = scroller.querySelectorAll<HTMLElement>("[data-card]");
      if (!cards.length) return;
      const scrollerRect = scroller.getBoundingClientRect();
      let closestIndex = 0;
      let closestDistance = Infinity;
      cards.forEach((card, i) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left - scrollerRect.left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });
      setActiveIndex(closestIndex);
    };
    scroller.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToCard = (direction: "prev" | "next") => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const cards = scroller.querySelectorAll<HTMLElement>("[data-card]");
    if (!cards.length) return;
    const targetIndex = direction === "next" ? Math.min(activeIndex + 1, cards.length - 1) : Math.max(activeIndex - 1, 0);
    const target = cards[targetIndex];
    if (target) {
      scroller.scrollTo({
        left: target.offsetLeft,
        behavior: "smooth"
      });
    }
  };
  const totalCards = highlights.length;
  const counterCurrent = String(activeIndex + 1).padStart(2, "0");
  const counterTotal = String(totalCards).padStart(2, "0");
  const progressPercent = (activeIndex + 1) / totalCards * 100;
  return <section data-section-id="1640" id="explore-favorite-destinations" className="bg-background text-foreground relative py-24 md:py-36 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute -top-8 right-4 md:right-16 select-none font-serif font-bold leading-none opacity-[0.06]" style={{
      fontSize: "clamp(12rem, 28vw, 22rem)"
    }}>
        02
      </div>

      <div ref={headerRef} className="relative mx-auto max-w-[1360px] px-[clamp(1rem,4vw,3rem)] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end">
          <div className={`md:col-span-7 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-default text-xs font-bold uppercase tracking-[0.18em] text-primary">MARGA TRAVEL №02</span>
              <span aria-hidden="true" className="inline-block w-2 h-2 bg-primary" />
              <span className="font-default text-xs font-bold uppercase tracking-[0.18em] text-foreground">
                DESTINATION FAVORITES
              </span>
            </div>
            <h2 className="font-default font-semibold uppercase leading-[0.94] tracking-tight text-primary" style={{
            fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
          }}>
              CURATED{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-white px-3 py-1 bg-primary">
                  GETAWAYS
                </span>
              </span>{" "}
              FOR YOU
            </h2>
          </div>

          <div className={`md:col-span-5 md:text-right transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] delay-100 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <div className="font-default font-semibold text-primary leading-none" style={{
            fontSize: "clamp(3rem, 6vw, 5rem)"
          }}>
              <span className="text-primary">{counterCurrent}</span>
              <span className="text-foreground/40"> / {counterTotal}</span>
            </div>
            <p className="mt-3 font-default text-xs uppercase tracking-[0.12em] text-foreground/60">
              Explore our top picks ↔
            </p>
          </div>
        </div>
      </div>

      <div aria-hidden="true" className="mt-12 md:mt-16 h-[14px] w-full bg-primary" />

      <div className="mt-10 md:mt-14">
        <div ref={scrollerRef} className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-6 px-[clamp(1rem,4vw,3rem)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" tabIndex={0}>
          {highlights.map((item, i) => <article data-index={i} data-card key={i} className="group snap-start shrink-0 w-[calc(100vw-2rem)] sm:w-[280px] md:w-[420px] h-[480px] md:h-[540px] bg-card text-card-foreground relative border border-border rounded-lg shadow-md flex flex-col overflow-hidden">
              <div className="relative overflow-hidden h-[60%] w-full">
                <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.05]" />
              </div>

              <div className="flex-1 flex flex-col p-5 md:p-6">
                <div className="font-default text-xs uppercase tracking-[0.12em] text-primary mb-3">
                  {item.date}
                </div>
                <h3 className="font-default font-semibold uppercase text-xl md:text-2xl leading-tight text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="font-default text-sm md:text-base leading-relaxed text-foreground/80 mb-4 flex-1">
                  {item.description}
                </p>
                <Link to={`/ShopPage?category=${item.categorySlug}`} className="inline-flex items-center gap-2 font-default text-xs font-bold uppercase tracking-[0.12em] text-secondary self-start">
                  <span className="border-b border-secondary pb-0.5 text-primary">
                    BOOK NOW →
                  </span>
                </Link>
              </div>
            </article>)}
        </div>

        <div className="mx-auto max-w-[1360px] px-[clamp(1rem,4vw,3rem)] mt-8">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="flex-1 relative h-px bg-border">
              <div className="absolute top-0 left-0 h-px bg-secondary transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]" style={{
              width: `${progressPercent}%`
            }} />
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={() => scrollToCard("prev")} disabled={activeIndex === 0} aria-label="Previous destination" className="font-default text-xs font-bold uppercase tracking-[0.06em] bg-white text-foreground border border-border px-4 py-3 h-auto hover:bg-secondary hover:text-white disabled:opacity-40 transition-colors duration-300">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button onClick={() => scrollToCard("next")} disabled={activeIndex === totalCards - 1} aria-label="Next destination" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive has-[>svg]:px-3 font-default text-xs font-bold uppercase tracking-[0.06em] text-white px-4 py-3 h-auto hover:bg-secondary/90 disabled:opacity-40 shadow-md bg-primary">
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}